import {
    ElementDatasourceConfiguration,
    ElementDefinition,
    FormPresetData,
} from '@form-engine/core-application/types/form';
import {
    getItemBy,
    relocateItem,
    resolveItemId,
} from '@form-engine/core-util/element-list';
import { insertItemAfter } from '@form-engine/core-util/element-list';
import {
    ElementListItem,
    cloneItemBranch,
    insertItemBefore,
} from '@form-engine/core-util/element-list';
import { isPlainObject } from '@form-engine/core-util/type-guards';
import uuidv4 from '@form-engine/core-util/uuid';

export function createPresetDataFromElement(
    form: FormDefinition,
    elementId: string,
    includeDatasources = true
) {
    const presetData: FormPresetData = {};

    if (includeDatasources) {
        presetData.datasources = [];
        presetData.elements = cloneItemBranch(
            form?.elements ?? [],
            elementId,
            undefined,
            (item, clone) => {
                if (item.id === elementId) {
                    delete clone.parent;
                }

                if (
                    !isPlainObject(item.datasource) ||
                    !isPlainObject(clone.datasource)
                )
                    return;
                const config: Partial<ElementDatasourceConfiguration> =
                    item.datasource;
                if (
                    config.mode !== 'static' &&
                    config.datasourceId &&
                    clone.datasource
                ) {
                    const [datasource] = cloneItemBranch(
                        form.datasources ?? [],
                        config.datasourceId
                    );
                    if (datasource && presetData.datasources) {
                        clone.datasource = {
                            ...item.datasource,
                            datasourceId: datasource.id,
                        };
                        presetData.datasources.push(datasource);
                    }
                }
            }
        );
    } else {
        presetData.elements = cloneItemBranch(
            form?.elements ?? [],
            elementId,
            undefined,
            (item, clone) => {
                if (item.id === elementId) {
                    delete clone.parent;
                }
            }
        );
    }

    return structuredClone(presetData);
}

const DEFAULT_RENEW_COLLECTION_IDS: (keyof FormDefinition)[] = [
    'elements',
    'workflowLayers',
    'datasources',
    'constants',
];

export function renewPresetDataIds(
    data: FormPresetData,
    renewCollections: (keyof FormDefinition)[] = DEFAULT_RENEW_COLLECTION_IDS
) {
    const staleIds = renewCollections.reduce((ids, collectionKey) => {
        const collection: ElementListItem[] =
            (data[collectionKey] as ElementListItem[]) ?? [];
        for (const item of collection) {
            if (item?.id && !ids.includes(item.id)) ids.push(item.id);
        }
        return ids;
    }, [] as string[]);

    let jsonData = JSON.stringify(data);
    for (const id of staleIds) {
        jsonData = jsonData.replaceAll(id, uuidv4());
    }

    return JSON.parse(jsonData);
}

type ED = ElementDefinition;

export function applyPresetData(
    data: FormPresetData,
    sourceForm: FormDefinition,
    elementDestination?: string | ED,
    elementInsertionParent?: string | ED,
    elementInsertionMethod: (
        items: readonly ED[],
        target: ED,
        destination?: string | ED
    ) => ED[] = insertItemBefore<ED>
) {
    const form = structuredClone(sourceForm);

    const {
        elements,
        datasources,
        workflowLayers,
        constants,
        ...mergeableData
    } = renewPresetDataIds(data);

    Object.assign(form, mergeableData);

    const [rootElement, ...childElements] = elements ?? [];

    if (rootElement) {
        if (elementInsertionParent) {
            rootElement.parent = resolveItemId(elementInsertionParent);
        }

        form.elements = relocateItem(
            elementInsertionMethod,
            form.elements ?? [],
            rootElement,
            elementDestination
        );
        if (childElements.length) {
            for (const child of childElements.reverse()) {
                form.elements = insertItemAfter<ED>(
                    form.elements,
                    child,
                    rootElement.id
                );
            }
        }
    }

    /** @todo reassign wfl / datasource assignments on preset id's with name collisions  */

    if (datasources?.length) {
        form.datasources ??= [];
        for (const datasource of datasources) {
            if (!getItemBy(form.datasources, 'name', datasource)) {
                form.datasources.push(structuredClone(datasource));
            }
        }
    }

    if (workflowLayers?.length) {
        form.workflowLayers ??= [];
        for (const workflowLayer of workflowLayers) {
            if (!getItemBy(form.workflowLayers, 'name', workflowLayer)) {
                form.workflowLayers.push(structuredClone(workflowLayer));
            }
        }
    }

    if (constants?.length) {
        form.constants ??= [];
        for (const constant of constants) {
            if (!getItemBy(form.constants, 'name', constant)) {
                form.constants.push(structuredClone(constant));
            }
        }
    }

    return form;
}
